import { themeColors } from '/@src/utils/themeColors'
import { ComposerTranslation } from 'vue-i18n'
import dayjs from 'dayjs'
import * as en from 'apexcharts/dist/locales/en.json'
import * as cn from 'apexcharts/dist/locales/zh-cn.json'
import {
  DataObj,
  FilterDateType,
  SeriesData,
  VipObj,
} from '/@src/models/orderStatistics'

const colors = [
  themeColors.success,
  themeColors.accent,
  themeColors.info,
  themeColors.orange,
  themeColors.green,
  themeColors.danger,
  themeColors.lightText,
  themeColors.fadeGrey,
  themeColors.secondary,
  themeColors.accentMedium,
  themeColors.purple,
  themeColors.accentLight,
  themeColors.warning,
  themeColors.blue,
  themeColors.primaryLight,
  themeColors.primary,
  themeColors.accent,
]

/*
 * 定制化 处理时间
 * */
function handlerDate(filterDate: FilterDateType | string, datetime: Date) {
  let str = ''
  switch (filterDate) {
    case 'DAY':
      str = dayjs(datetime).format('MM/DD')
      break
    case 'WEEK':
      const day = new Date(datetime)
      const time = day.setDate(day.getDate() + 6)
      str = `${dayjs(datetime).format('MM/DD')}～${dayjs(time).format('MM/DD')}`
      break
    case 'MONTH':
      str = dayjs(datetime).format('MM') + '月'
      break
    case 'QUARTER':
      str = dayjs(datetime).format('MM/DD')
      const YYYY = dayjs(datetime).format('YYYY')
      let month: number = 0
      switch (dayjs(datetime).format('MM')) {
        case '01':
          month = dayjs(`${YYYY}-03`).daysInMonth()
          str += `～03/${month}`
          break
        case '04':
          month = dayjs(`${YYYY}-06`).daysInMonth()
          str += `～06/${month}`
          break
        case '07':
          month = dayjs(`${YYYY}-09`).daysInMonth()
          str += `～09/${month}`
          break
        case '10':
          month = dayjs(`${YYYY}-12`).daysInMonth()
          str += `～12/${month}`
          break
      }
      break
  }

  return str
}

/**
 * 设置收入图表
 * @param t
 * @param locale
 * @param total
 * @param SeriesData
 * @param start
 */
function setupAmountChartOption(
  isShowTitle: boolean,
  t: ComposerTranslation,
  locale: string,
  total: number,
  SeriesData: number[],
  start: Date,
  period: number = 30
) {
  return {
    chart: {
      id: 'salesAnalytics',
      group: 'dashboardChart',
      type: 'area',
      defaultLocale: locale.toLocaleLowerCase(),
      locales: [en, cn],
      height: 130,
      sparkline: {
        enabled: true,
      },
    },
    colors: [themeColors.orange],
    stroke: {
      width: [2],
      curve: 'straight',
    },
    fill: {
      opacity: 1,
    },
    series: [
      {
        name: t('text-series-revenue'),
        data: SeriesData,
      },
    ],
    labels: [...Array(period).keys()].map((n) => {
      return dayjs(new Date(start.getTime() + 3600 * 1000 * 24 * n)).format(
        'YYYY-MM-DD'
      )
    }),
    yaxis: {
      min: 0,
      show: false,
      labels: {
        minWidth: 200,
      },
    },
    xaxis: {
      type: 'datetime',
    },
    title: {
      text: isShowTitle ? t('analytics-field-revenue') : '',
      offsetX: 5,
      style: {
        fontSize: '24px',
        cssClass: 'apexcharts-yaxis-title',
        color: themeColors.lightText,
      },
    },
    subtitle: {
      text: isShowTitle
        ? '￥' + parseFloat((total / 100).toFixed(2)).toLocaleString()
        : '',
      offsetX: 5,
      style: {
        fontSize: '24px',
        fontWeight: '600',
        cssClass: 'apexcharts-yaxis-title',
      },
    },
  }
}

/**
 * 设置订单图表
 * @param t
 * @param locale
 * @param total
 * @param SeriesData
 * @param start
 */
function setupOrderChartOption(
  t: ComposerTranslation,
  locale: string,
  total: number,
  SeriesData: number[],
  start: Date,
  period: number = 30
) {
  return {
    chart: {
      id: 'orderAnalytics',
      // group: 'statisticsChart',
      type: 'area',
      defaultLocale: locale.toLocaleLowerCase(),
      locales: [en, cn],
      height: 130,
      sparkline: {
        enabled: true,
      },
    },
    colors: [themeColors.primary],
    stroke: {
      width: [2],
      curve: 'straight',
    },
    fill: {
      opacity: 1,
    },
    series: [
      {
        name: t('text-series-order'),
        data: SeriesData,
      },
    ],
    labels: [...Array(period).keys()].map((n) => {
      return dayjs(new Date(start.getTime() + 3600 * 1000 * 24 * n)).format(
        'YYYY-MM-DD'
      )
    }),
    yaxis: {
      min: 0,
      labels: {
        minWidth: 100,
      },
    },
    xaxis: {
      type: 'datetime',
    },
    title: {
      text: t('analytics-field-orders'),
      offsetX: 5,
      style: {
        fontSize: '24px',
        cssClass: 'apexcharts-yaxis-title',
        color: themeColors.lightText,
      },
    },
    subtitle: {
      text: total,
      offsetX: 5,
      style: {
        fontSize: '24px',
        fontWeight: '600',
        cssClass: 'apexcharts-yaxis-title',
      },
    },
  }
}

function setupOrderTypesChartOptions(
  types: Map<string, number>,
  t: ComposerTranslation,
  locale: string
) {
  const series = []
  for (const value of types.values()) {
    series.push(value)
  }
  const labels: string[] = []
  for (const key of types.keys()) {
    labels.push(key)
  }

  //将0的数据删选出去
  const newSeries: number[] = []
  const newLabels: string[] = []
  series.forEach((x, i) => {
    if (x !== 0) {
      newSeries.push(x)
      newLabels.push(labels[i])
    }
  })
  return {
    series: newSeries,
    chart: {
      height: 295,
      type: 'pie',
    },
    colors: [
      themeColors.orange,
      themeColors.purple,
      themeColors.success,
      themeColors.info,
      themeColors.danger,
      themeColors.lightText,
    ],
    labels: newLabels,
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            width: 315,
            toolbar: {
              show: false,
            },
          },
          legend: {
            position: 'top',
          },
        },
      },
    ],
    legend: {
      position: 'right',
      horizontalAlign: 'center',
    },
    title: {
      text: t('analytics-field-pay-product'),
      offsetX: 5,
      style: {
        fontSize: '24px',
        cssClass: 'apexcharts-yaxis-title',
        color: themeColors.lightText,
      },
    },
  }
}

/**
 * 订单统计 - 收入统计图表
 * @param t
 * @param locale
 * @param SeriesData
 * @param labels
 * @param fileterDate
 */
function setupRevenueStatisticsChartOptions(
  t: ComposerTranslation,
  locale: string,
  SeriesData: number[],
  labels: Date[],
  fileterDate: FilterDateType | string
) {
  return {
    chart: {
      id: 'statistics',
      type: 'line',
      // group: 'statisticsChart',
      defaultLocale: locale.toLocaleLowerCase(),
      locales: [en, cn],
      height: 270,
      sparkline: {
        enabled: false,
      },
      zoom: {
        enabled: false,
      },
    },
    colors: [themeColors.primary],
    stroke: {
      width: [2],
      curve: 'smooth',
    },
    fill: {
      opacity: 1,
    },
    series: [
      {
        name: t('field-revenue'),
        data: SeriesData.map((x) => x / 100),
      },
    ],
    dataLabels: {
      enabled: false,
    },
    labels: labels.map((n) => {
      return handlerDate(fileterDate, n)
    }),
    yaxis: {
      opposite: false,
    },
    // xaxis: {
    //   type: 'datetime',
    // },
  }
}

/**
 * 订单统计 - 订单数
 * @param t
 * @param locale
 * @param SeriesData
 * @param labels
 */
function setupStatisticsOrderChartOption(
  t: ComposerTranslation,
  locale: string,
  SeriesData: number[],
  labels: Date[]
) {
  return {
    chart: {
      id: 'statistics',
      // group: 'statisticsChart',
      type: 'area',
      defaultLocale: locale.toLocaleLowerCase(),
      locales: [en, cn],
      height: 130,
      sparkline: {
        enabled: true,
      },
    },
    colors: [themeColors.primary],
    stroke: {
      width: [2],
      curve: 'smooth',
    },
    fill: {
      opacity: 1,
    },
    series: [
      {
        name: t('field-orders-count'),
        data: SeriesData,
      },
    ],
    labels: labels.map((n) => {
      return dayjs(n).format('YYYY-MM-DD')
    }),
    yaxis: {
      min: 0,
      labels: {
        minWidth: 100,
      },
    },
    xaxis: {
      type: 'datetime',
    },
  }
}

/**
 * 订单统计 - 订单概述
 * @param t
 * @param locale
 * @param SeriesData
 * @param labels
 * @param filterDate
 */
function setupCommodityDetailsChartOptions(
  t: ComposerTranslation,
  locale: string,
  SeriesData: object[],
  labels: Date[],
  filterDate: FilterDateType | string
) {
  return {
    chart: {
      id: 'statistics',
      // group: 'statisticsChart',
      type: 'area',
      defaultLocale: locale.toLocaleLowerCase(),
      locales: [en, cn],
      height: 302,
      sparkline: {
        enabled: false,
      },
      zoom: {
        enabled: false,
      },
    },
    colors,
    stroke: {
      width: [2],
      curve: 'smooth',
    },
    fill: {
      opacity: 1,
    },
    series: SeriesData.map((x) => ({
      ...x,
      data: x.counts,
    })),
    labels: labels.map((n) => {
      return handlerDate(filterDate, n)
    }),
    // yaxis: {
    //   min: 0,
    //   labels: {
    //     minWidth: 100,
    //   },
    // },
    dataLabels: {
      enabled: false,
    },
    // xaxis: {
    //   type: 'datetime',
    // },
  }
}

/**
 * 订单统计 - 商品明细
 * @param t
 * @param locale
 * @param SeriesData
 * @param labels
 */
function setupCommodityProportionChartOptions(
  t: ComposerTranslation,
  locale: string,
  SeriesData: number[],
  labels: string[]
) {
  //将0的数据删选出去
  const newSeries: number[] = []
  const newLabels: string[] = []
  SeriesData.forEach((x, i) => {
    if (x !== 0) {
      newSeries.push(x)
      newLabels.push(labels[i])
    }
  })
  return {
    series: newSeries,
    chart: {
      height: 350,
      type: 'pie',
    },
    colors,
    labels: newLabels,
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            width: 315,
            toolbar: {
              show: false,
            },
          },
          legend: {
            position: 'top',
          },
        },
      },
    ],
    legend: {
      position: 'bottom',
      horizontalAlign: 'left',
    },
  }
}

/**
 * 订单统计 - 平台概括 支付方式
 * @param t
 * @param locale
 * @param SeriesData
 * @param labels
 * @param type 金额 数字
 * @param date
 * @param colorList
 */
function setupPayChannelOption(
  t: ComposerTranslation,
  locale: string,
  SeriesData: SeriesData[],
  labels: Date[],
  type: string,
  date: FilterDateType | string,
  colorList: string[] = colors
) {
  const dates = labels.map((n) => {
    return handlerDate(date, n)
  })
  if (type === 'amounts') {
    SeriesData = SeriesData.map((x: SeriesData) => ({
      ...x,
      data: x.data.map((x) => x / 100),
    })).filter((x) => !['FREE OR CHARGE', '免单'].includes(x.name))
  }
  return {
    chart: {
      id: 'payChannel-chart',
      defaultLocale: locale.toLocaleLowerCase(),
      locales: [en, cn],
      height: 230,
      zoom: false,
    },
    colors: [...colorList, ...colors].filter((x) => x !== ''),
    dataLabels: {
      enabled: type === 'counts' && date !== 'DAY',
    },
    stroke: {
      show: true,
      width: 1,
    },
    fill: {
      opacity: 1,
    },
    series: SeriesData,
    labels: dates,
    tooltip: {
      enabled: true,
      y: SeriesData.map((x, i) => {
        return {
          formatter: function () {
            return ``
          },
          title: {
            formatter: (seriesName: string, val: any) => {
              const { series, dataPointIndex } = val
              return `${seriesName}: ${series[i][dataPointIndex]}${
                type === 'amounts' ? '元' : '单'
              }`
            },
          },
        }
      }),
    },
  }
}

/**
 * 收入统计 - 当前周期最新数据的比值
 * @param series
 * @param width
 * @param height
 */
function setupRevenuePencChartOption(series: number) {
  return {
    chart: {
      type: 'radialBar',
      height: 170,
      zoom: {
        enabled: false,
      },
    },
    colors: [themeColors.info],
    plotOptions: {
      radialBar: {
        hollow: {
          size: '70%',
        },
        dataLabels: {
          name: {
            show: false,
          },
          value: {
            offsetY: 0,
          },
        },
      },
    },
    series: [series],
    theme: {
      monochrome: {
        enabled: false,
      },
    },
    legend: {
      show: false,
    },
  }
}

/**
 * 收入统计 - 选择时间的详情
 * @param t
 * @param series
 * @param labels
 * @param type
 * @param colors
 * @param dateStr
 */
function setupRadialBarOption(
  t: ComposerTranslation,
  series: number[],
  labels: string[],
  type: string,
  colors: string[],
  dateStr: string
) {
  const unit = type !== 'count' ? '元' : '单'
  const name =
    type !== 'count'
      ? type === 'blackGold'
        ? t('text-member-blackgold')
        : t('text-member-meta')
      : t('field-orders-count')
  return {
    chart: {
      type: 'radialBar',
      height: 120,
      zoom: {
        enabled: false,
      },
      offsetY: -20,
    },
    colors,
    series: [series[0]],
    theme: {
      monochrome: {
        enabled: false,
      },
    },
    plotOptions: {
      radialBar: {
        hollow: {
          size: '50%',
        },
        dataLabels: {
          name: {
            show: false,
          },
          value: {
            show: true,
            fontSize: '12px',
            offsetY: 5,
            color: colors,
            formatter: function (val) {
              return val.toFixed(2) + '%'
            },
          },
        },
      },
    },
    labels,
    dataLabels: {
      enabled: false,
    },
    legend: {
      show: false,
    },
    tooltip: {
      enabled: true,
      y: {
        formatter: function () {
          return ``
        },
        title: {
          formatter: (seriesName) => {
            return `
                    ${dateStr}${name}: ${series[1]}${unit}
                    <br>
                    为${seriesName}的${
              series[2] === 0
                ? series[1]
                : ((series[1] / series[2]) * 100).toFixed(2)
            }%`
          },
        },
      },
    },
  }
}

/**
 * 订单统计 - 平台明细 支付明细
 * @param t
 * @param locale
 * @param data
 * @param categories
 * @param type
 */
function setupPayTypeDetailOption(
  t: ComposerTranslation,
  locale: string,
  data: number[],
  categories: string[],
  type: string
) {
  if (type === 'amounts') {
    const index = categories.findIndex(
      (x) => x === '免单' || x === 'FREE OR CHARGE'
    )
    if (index > -1) {
      categories.splice(index, 1)
      data.splice(index, 1)
    }
  }
  for (let i = 1; i < data.length; i++) {
    const num = data[i]
    const name = categories[i]
    let j = i
    while (j > 0) {
      if (data[j - 1] > num) {
        data[j] = data[j - 1]
        categories[j] = categories[j - 1]
      } else {
        break
      }
      j -= 1
    }
    data[j] = num
    categories[j] = name
  }
  data =
    type === 'amounts' ? data.reverse().map((x) => x / 100) : data.reverse()
  categories = categories.reverse()
  return {
    chart: {
      id: 'payChannel-chart',
      type: 'bar',
      defaultLocale: locale.toLocaleLowerCase(),
      locales: [en, cn],
      height: 245,
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        horizontal: true,
      },
    },
    dataLabels: {
      enabled: false,
    },
    xaxis: {
      categories,
    },
    colors: [themeColors.primary],
    series: [
      {
        name:
          type === 'amounts'
            ? t('analytics-pay-amount')
            : t('analytics-pay-count'),
        data,
      },
    ],
    tooltip: {
      enabled: true,
      y: {
        formatter: function () {
          return ``
        },
        title: {
          formatter: (seriesName, val) => {
            const { series, dataPointIndex, seriesIndex } = val
            return `${seriesName}: ${series[seriesIndex][dataPointIndex]}${
              type === 'amounts' ? '元' : '单'
            }`
          },
        },
      },
    },
  }
}

export {
  setupAmountChartOption,
  setupOrderChartOption,
  setupOrderTypesChartOptions,
  setupRevenueStatisticsChartOptions,
  setupStatisticsOrderChartOption,
  setupCommodityDetailsChartOptions,
  setupCommodityProportionChartOptions,
  setupPayChannelOption,
  setupRevenuePencChartOption,
  setupRadialBarOption,
  setupPayTypeDetailOption,
}
